import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormHelperText, Box } from '@mui/material';
import { Typography } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import InsuranceConventionCoefficientEditComponent from './InsuranceConventionCoefficientEditComponent';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import { actionsSlotId } from './InsuranceConventionCoefficientModalComponent';
import { INSURANCE_ADDITIONAL_DATA__TYPES, INSURANCE_ADDITIONAL_DATA__TYPE__DETAILS } from 'features/insuranceConventions/edit/queries';
import { FULL_GENERIC_FILTER_TEMPLATE } from 'features/genericFilterTemplates/edit/queries';
import { ADD_UPDATE_INSURANCE_CONVENTION_ADDITIONAL_DATA } from 'features/insuranceConventions/edit/mutations';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import {
  shape,
  required,
  fromParent,
  when,
  concatFailure,
  isValid,
  getErrors,
  Validator,
  Failure,
  Success,
  ValidationError
} from '@totalsoft/pure-validations';
import { isDirty } from '@totalsoft/change-tracking';
import { oneFieldShouldBeCompleted } from 'utils/customValidators';
import { editCoefficientVar } from 'features/insuranceConventions/edit/variables';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useQuery, useMutation } from 'hooks/apollo';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { usePermission } from 'providers/RightsHooks';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';

const allowMultipleDetailsCoefficient = (coefficients, insuranceAdditionalDataTypes) =>
  Validator(content => {
    if (!content?.isDefault || !content.additionalDataTypeId || !coefficients || !insuranceAdditionalDataTypes) return Success;

    const additionalDataType = insuranceAdditionalDataTypes.find(x => x.additionalDataTypeId === content.additionalDataTypeId);
    if (!additionalDataType || additionalDataType.allowMultipleDetails === true) return Success;

    const coefficientsByAdditionalDataType = coefficients.filter(
      coefficient =>
        coefficient.additionalDataTypeId === content.additionalDataTypeId &&
        coefficient.insuranceConventionAdditionalDataId !== content.insuranceConventionAdditionalDataId
    );
    return coefficientsByAdditionalDataType.some(coefficient => coefficient.isDefault === true)
      ? Failure(
          ValidationError(
            i18next.t('InsuranceConventions.Details.Coefficients.MultipleDefaultDetailsNotAllowed', {
              coefficientName: additionalDataType.additionalDataTypeName
            })
          )
        )
      : Success;
  });

const insuranceConventionCoefficientValidator = (coefficients, insuranceAdditionalDataTypes) =>
  [
    shape({
      additionalDataTypeId: required,
      detailId: required,
      relativePercent: fromParent(parent => required |> when(!parent.percentFormula && !parent.amount && !parent.amountFormula)),
      percentFormula: fromParent(parent => required |> when(!parent.relativePercent && !parent.amount && !parent.amountFormula)),
      amount: fromParent(parent => required |> when(!parent.relativePercent && !parent.percentFormula && !parent.amountFormula)),
      amountFormula: fromParent(parent => required |> when(!parent.amount && !parent.percentFormula && !parent.relativePercent))
    }),
    oneFieldShouldBeCompleted(
      ['relativePercent', 'percentFormula', 'amount', 'amountFormula'],
      'InsuranceConventions.Details.Coefficients.ValidateMandatoryFields'
    ),
    allowMultipleDetailsCoefficient(coefficients, insuranceAdditionalDataTypes)
  ] |> concatFailure;

const InsuranceConventionCoefficientEditContainer = ({ coefficients }) => {
  const { t } = useTranslation();
  const editCoefficient = useReactiveVar(editCoefficientVar);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  // #region state
  const [coefficientLens, coefficientDirtyInfo, resetCoefficientLens] = useChangeTrackingLens({
    ...editCoefficient.coefficient,
    disabled: false,
    genericFilters: defaultTemplate
  });
  // #endregion

  // #region queries
  const { data: insuranceAdditionalDataTypes } = useQuery(INSURANCE_ADDITIONAL_DATA__TYPES);

  const { data: insuranceAdditionalDataTypeDetails } = useQuery(INSURANCE_ADDITIONAL_DATA__TYPE__DETAILS, {
    skip: !(coefficientLens.additionalDataTypeId |> get),
    variables: { additionalDataTypeId: coefficientLens.additionalDataTypeId |> get }
  });

  const { templates, parameters, operators, loadValues, loadPagedValues, loadMissingParamters, loadTemplate } = useGenericFilterData({
    context: GenericFilterContext.InsuranceConvention,
    withTemplateSelection: true
  });

  const { loading: genericFilterTemplateLoading } = useQuery(FULL_GENERIC_FILTER_TEMPLATE, {
    skip: !(coefficientLens.genericFilterTemplateId |> get),
    variables: { id: coefficientLens.genericFilterTemplateId |> get },
    onCompleted: data => {
      const generalData = data.genericFilterTemplateGeneralData;
      const templateData = {
        withTemplate: Boolean(generalData.isTemplate),
        templateId: generalData.id,
        template: {
          phrase: data.genericFilterTemplateDetails.phrase,
          conditions: data.genericFilterTemplateDetails.conditions || []
        }
      };
      if (templateData.withTemplate) loadMissingParamters(templateData.template.conditions);
      resetCoefficientLens(prev => ({ ...prev, genericFilters: templateData }));
      setInitialData(templateData);
    }
  });
  // #endregion

  // #region mutations
  const [saveInsuranceConventionAdditionalData] = useMutation(ADD_UPDATE_INSURANCE_CONVENTION_ADDITIONAL_DATA, {
    onError: () => {
      (coefficientLens.genericFilters.disabled |> set)(false);
      (coefficientLens.disabled |> set)(false);
    }
  });

  // #endregion

  // #region validator
  const coefficientValidator = useMemo(
    () => insuranceConventionCoefficientValidator(coefficients, insuranceAdditionalDataTypes?.insuranceAdditionalDataTypes),
    [coefficients, insuranceAdditionalDataTypes]
  );
  const [validation, validate, resetValidation] = useDirtyFieldValidation(coefficientValidator);
  // #endregion

  // #region handlers
  const handleAdditionalDataTypeChanged = useCallback(
    value => {
      (coefficientLens.additionalDataTypeId |> set)(value.additionalDataTypeId);
      (coefficientLens.additionalDataTypeName |> set)(value.additionalDataTypeName);
      (coefficientLens.detailId |> set)(null);
      (coefficientLens.detailName |> set)(null);
    },
    [coefficientLens.additionalDataTypeId, coefficientLens.additionalDataTypeName, coefficientLens.detailId, coefficientLens.detailName]
  );

  const handleAdditionalDataTypeDetailChanged = useCallback(
    value => {
      (coefficientLens.detailId |> set)(value.detailId);
      (coefficientLens.detailName |> set)(value.detailName);
    },
    [coefficientLens.detailId, coefficientLens.detailName]
  );

  const handleSaveCoefficient = useCallback(
    genericFiltersData => {
      const coefficientData = coefficientLens |> get;
      // eslint-disable-next-line no-unused-vars
      const { genericFilters, disabled, ...coefficient } = coefficientData;
      if (!validate(coefficient)) return;
      (coefficientLens.genericFilters.disabled |> set)(true);
      (coefficientLens.disabled |> set)(true);
      const input = {
        coefficient,
        genericFilters: genericFiltersData
      };
      saveInsuranceConventionAdditionalData({ variables: { input } });
    },
    [coefficientLens, saveInsuranceConventionAdditionalData, validate]
  );

  const handleResetGenericFiltersLens = useCallback(
    genericFiltersData => {
      resetCoefficientLens(prev => {
        if (genericFiltersData instanceof Function) return { ...prev, genericFilters: genericFiltersData(prev.genericFilters) };
        else return { ...editCoefficient.coefficient, disabled: false, genericFilters: genericFiltersData };
      });
      resetValidation();
    },
    [editCoefficient.coefficient, resetCoefficientLens, resetValidation]
  );
  // #endregion

  useEffect(() => {
    const coefficient = coefficientLens |> get;
    validate(coefficient, coefficientDirtyInfo);
  }, [coefficientLens, coefficientDirtyInfo, validate]);

  useEffect(() => {
    resetCoefficientLens(prev => ({
      ...prev,
      disabled: !canWrite,
      genericFilters: {
        ...prev.genericFilters,
        disabled: !canWrite
      }
    }));
  }, [canWrite, resetCoefficientLens]);

  return (
    <Box sx={{ p: 1 }}>
      <InsuranceConventionCoefficientEditComponent
        coefficientLens={coefficientLens}
        validation={validation}
        insuranceAdditionalDataTypes={insuranceAdditionalDataTypes?.insuranceAdditionalDataTypes}
        insuranceAdditionalDataTypeDetails={insuranceAdditionalDataTypeDetails?.insuranceAdditionalDataTypeDetails}
        onAdditionalDataTypeChanged={handleAdditionalDataTypeChanged}
        onAdditionalDataTypeDetailChanged={handleAdditionalDataTypeDetailChanged}
      />
      <Grid sx={{ mt: 1 }}>
        {!isValid(validation) && isDirty(coefficientDirtyInfo) && (
          <FormHelperText error={true} id='name-error-text'>
            {getErrors(validation)}
          </FormHelperText>
        )}
      </Grid>
      <Grid sx={{ mt: 3 }}>
        <Typography variant='subtitle1' color='primary' fontWeight='bold' sx={{ mb: 3 }}>
          {t('EntityFilter.Filters')}
        </Typography>
        {genericFilterTemplateLoading ? (
          <WrappedFakeText lines={5} />
        ) : (
          <GenericFilterComposer
            lens={coefficientLens.genericFilters}
            dirtyInfo={coefficientDirtyInfo}
            resetLens={handleResetGenericFiltersLens}
            templates={templates}
            parameters={parameters}
            operators={operators}
            loadValues={loadValues}
            loadPagedValues={loadPagedValues}
            loadTemplate={loadTemplate}
            onSave={handleSaveCoefficient}
            conditionsViewMode='table'
            withTemplateGeneralData
            withTemplateSelection
            forwardProps={{ container: { mt: -3, mb: 2 } }}
            actionsSlotId={actionsSlotId}
          />
        )}
      </Grid>
    </Box>
  );
};

InsuranceConventionCoefficientEditContainer.propTypes = {
  coefficients: PropTypes.array
};

export default InsuranceConventionCoefficientEditContainer;
